import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Chip, CircularProgress, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Grid, MenuItem, IconButton,
  Tooltip, TablePagination, Alert, Avatar, Stack, Divider, Paper
} from '@mui/material';
import {
  Add, Search, Refresh, Edit, SupportAgent, Visibility, AssignmentInd,
  Person, AccessTime, FiberManualRecord
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuthStore } from '../../store/auth';

const empty = { subject: '', description: '', priority: 'medium', category: 'technical', status: 'open', assigned_to: '' };
const STATUS_COLOR: any   = { open: 'warning', in_progress: 'info', resolved: 'success', closed: 'default' };
const PRIORITY_COLOR: any = { low: 'default', medium: 'info', high: 'warning', critical: 'error' };
const fmtDate = (d: any) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';

// ── Ticket Detail Dialog ────────────────────────────────────────────────────
const TicketDetail: React.FC<{ ticket: any; users: any[]; onClose: () => void; onEdit: () => void }> = ({ ticket: t, users, onClose, onEdit }) => (
  <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <SupportAgent color="primary" />
        <Typography fontWeight={700}>Ticket #{t.id?.slice(-8).toUpperCase()}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Chip label={t.status?.replace('_',' ')} size="small" color={STATUS_COLOR[t.status] || 'default'} />
        <Chip label={t.priority} size="small" color={PRIORITY_COLOR[t.priority] || 'default'} />
      </Stack>
    </DialogTitle>
    <DialogContent>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>{t.subject}</Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">Category</Typography>
          <Typography variant="body2" fontWeight={600}>{t.category?.replace('_',' ').replace(/\b\w/g, (l:string)=>l.toUpperCase()) || '—'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">Created</Typography>
          <Typography variant="body2">{fmtDate(t.created_at)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ display:'flex', alignItems:'center', gap:0.5 }}><Person sx={{ fontSize:14 }}/> Raised By</Typography>
          <Typography variant="body2" fontWeight={600}>{t.created_by_name || '—'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ display:'flex', alignItems:'center', gap:0.5 }}><AssignmentInd sx={{ fontSize:14 }}/> Assigned To</Typography>
          {t.assigned_to_name
            ? <Stack direction="row" spacing={1} alignItems="center">
                <Avatar sx={{ width:24, height:24, fontSize:12, bgcolor:'primary.main' }}>{t.assigned_to_name[0]}</Avatar>
                <Typography variant="body2" fontWeight={600}>{t.assigned_to_name}</Typography>
              </Stack>
            : <Typography variant="body2" color="text.secondary">Unassigned</Typography>}
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary" display="block">Last Updated</Typography>
          <Typography variant="body2">{fmtDate(t.updated_at)}</Typography>
        </Grid>
      </Grid>
      {t.description && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>Description</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{t.description}</Typography>
        </Paper>
      )}
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose}>Close</Button>
      <Button variant="contained" startIcon={<Edit />} onClick={onEdit}>Edit Ticket</Button>
    </DialogActions>
  </Dialog>
);

// ── Main Page ───────────────────────────────────────────────────────────────
export const TicketsPage: React.FC = () => {
  const { user: me } = useAuthStore();
  const [data, setData]     = useState<any[]>([]);
  const [total, setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage]     = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [dialog, setDialog] = useState(false);
  const [detailTicket, setDetailTicket] = useState<any>(null);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm]     = useState<any>(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [users, setUsers]   = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '25', offset: String(page * 25) });
      if (search) p.set('search', search);
      if (statusFilter) p.set('status', statusFilter);
      const { data: res } = await api.get(`/tickets?${p}`);
      setData(res.data || (Array.isArray(res) ? res : []));
      setTotal(res.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/users?limit=100').then(({ data: r }) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, assigned_to: me?.id || '' });
    setError(''); setDialog(true);
  };
  const openEdit = (row: any) => {
    setEditing(row);
    setForm({ subject: row.subject, description: row.description || '', priority: row.priority,
              category: row.category || 'technical', status: row.status, assigned_to: row.assigned_to || '' });
    setError(''); setDetailTicket(null); setDialog(true);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      if (editing) await api.put(`/tickets/${editing.id}`, payload);
      else await api.post('/tickets', payload);
      setDialog(false); load();
    } catch (e: any) { setError(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const statusCounts = ['open','in_progress','resolved','closed'].map(s => ({
    s, count: data.filter(d => d.status === s).length
  }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb: 3 }}>
        <Box sx={{ display:'flex', alignItems:'center', gap: 1.5 }}>
          <SupportAgent sx={{ color:'primary.main', fontSize:32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Support Tickets</Typography>
            <Typography variant="body2" color="text.secondary">{total} tickets total</Typography>
          </Box>
        </Box>
        <Box sx={{ display:'flex', gap:1 }}>
          <TextField select size="small" label="Status" value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }} sx={{ width:140 }}>
            <MenuItem value="">All</MenuItem>
            {['open','in_progress','resolved','closed'].map(s =>
              <MenuItem key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</MenuItem>
            )}
          </TextField>
          <TextField size="small" placeholder="Search…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment> }}
            sx={{ width:200 }} />
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Ticket</Button>
        </Box>
      </Box>

      {/* Status summary chips */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {['open','in_progress','resolved','closed'].map(s => (
          <Chip key={s} size="small"
            label={`${s.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}: ${data.filter(d=>d.status===s).length}`}
            color={STATUS_COLOR[s] || 'default'}
            variant={statusFilter === s ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            sx={{ cursor:'pointer' }}
          />
        ))}
      </Stack>

      {/* Table */}
      <Card>
        {loading ? <Box sx={{ p:6, textAlign:'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#','Subject','Category','Priority','Status','Raised By','Assigned To','Created','Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight:700, whiteSpace:'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => (
                  <TableRow key={row.id} hover sx={{ cursor:'pointer' }}>
                    <TableCell sx={{ fontFamily:'monospace', color:'text.secondary', fontSize:11 }}>
                      #{row.id?.slice(-6).toUpperCase()}
                    </TableCell>
                    <TableCell sx={{ fontWeight:600, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {row.subject}
                    </TableCell>
                    <TableCell sx={{ color:'text.secondary', whiteSpace:'nowrap' }}>
                      {row.category?.replace('_',' ').replace(/\b\w/g,(l:string)=>l.toUpperCase()) || '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.priority} size="small" color={PRIORITY_COLOR[row.priority]||'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={row.status?.replace('_',' ')} size="small" color={STATUS_COLOR[row.status]||'default'} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace:'nowrap' }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Avatar sx={{ width:20, height:20, fontSize:10, bgcolor:'grey.400' }}>
                          {(row.created_by_name||'?')[0]}
                        </Avatar>
                        <Typography variant="body2">{row.created_by_name || '—'}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ whiteSpace:'nowrap' }}>
                      {row.assigned_to_name
                        ? <Stack direction="row" spacing={0.5} alignItems="center">
                            <Avatar sx={{ width:20, height:20, fontSize:10, bgcolor:'primary.main' }}>
                              {row.assigned_to_name[0]}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>{row.assigned_to_name}</Typography>
                          </Stack>
                        : <Typography variant="body2" color="text.disabled">Unassigned</Typography>}
                    </TableCell>
                    <TableCell sx={{ color:'text.secondary', fontSize:12, whiteSpace:'nowrap' }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace:'nowrap' }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => setDetailTicket(row)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Ticket">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py:6, color:'text.secondary' }}>
                      No tickets found. Click "New Ticket" to raise a support issue.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={25}
              onPageChange={(_,p) => setPage(p)} rowsPerPageOptions={[25]} />
          </>
        )}
      </Card>

      {/* Ticket Detail View */}
      {detailTicket && (
        <TicketDetail ticket={detailTicket} users={users}
          onClose={() => setDetailTicket(null)}
          onEdit={() => openEdit(detailTicket)} />
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Update Ticket' : 'New Support Ticket'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb:2 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth label="Subject *" value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Description" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the issue in detail…" />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Priority" value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}>
                {['low','medium','high','critical'].map(p =>
                  <MenuItem key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Category" value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}>
                {['technical','billing','general','feature_request'].map(c =>
                  <MenuItem key={c} value={c}>
                    {c.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Status" value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                {['open','in_progress','resolved','closed'].map(s =>
                  <MenuItem key={s} value={s}>
                    {s.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Assign To" value={form.assigned_to}
                onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                <MenuItem value=""><em>Unassigned</em></MenuItem>
                {users.map(u => (
                  <MenuItem key={u.id} value={u.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width:24, height:24, fontSize:11, bgcolor:'primary.main' }}>
                        {(u.first_name||u.email)[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {u.first_name} {u.last_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{u.role?.replace(/_/g,' ')}</Typography>
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p:2 }}>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.subject}>
            {saving ? <CircularProgress size={20} /> : editing ? 'Update Ticket' : 'Create Ticket'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
